import React, { useEffect, useMemo, useState } from 'react';
import { Badge, Button, Drawer, Popover, Tag, Tooltip } from 'antd';
import { CloseOutlined, FilterOutlined } from '@ant-design/icons';
import { FilterFieldBlock, clearFields, fieldKeys, isEmpty, isFieldActive } from './controls';
import FilterButton from './FilterButton';
import { FilterField, FilterToolbarProps } from './types';
import './FilterToolbar.css';

const FilterToolbar = ({
    fields,
    values,
    onChange,
    collapseAfter,
    showActiveChips = true,
    showClearAll = true,
    pruneUnknownValues = false,
    layout = 'inline',
    filterButtonLabel = 'Filters',
    hideTrigger = false,
    open,
    onOpenChange,
    drawerTitle,
    drawerDescription,
    drawerFooter,
    extra,
    className,
    style,
}: FilterToolbarProps) => {
    const visibleFields = useMemo(() => fields.filter((f) => !f.hidden), [fields]);
    // What the toolbar actually draws. A field the page renders elsewhere still
    // belongs to the filter set — it is only left out of the layout.
    const panelFields = useMemo(() => visibleFields.filter((f) => !f.renderedElsewhere), [visibleFields]);
    const [ownOpen, setOwnOpen] = useState(false);
    const drawerOpen = open ?? ownOpen;
    const setDrawerOpen = (next: boolean) => {
        if (onOpenChange) onOpenChange(next);
        else setOwnOpen(next);
    };

    // Every filter key this page actually offers. Hidden fields are excluded on
    // purpose: a field hidden for this POV is not part of its filter set, so a
    // stored value for it would filter results with no visible control.
    const ownedKeys = useMemo(() => {
        const keys = new Set<string>();
        visibleFields.forEach((field) => fieldKeys(field).forEach((key) => keys.add(key)));
        return keys;
    }, [visibleFields]);

    // Clear leftovers from another page's filter set. Not mount-only: stored
    // filters can arrive after the first render, and this converges anyway —
    // once the stale keys are gone the signature is empty and it stops.
    const staleSignature = pruneUnknownValues
        ? Object.keys(values).filter((key) => !ownedKeys.has(key)).sort().join('|')
        : '';
    useEffect(() => {
        if (!staleSignature) return;
        const next = { ...values };
        staleSignature.split('|').forEach((key) => delete next[key]);
        onChange(next);
    }, [staleSignature]);

    const wrap = (field: FilterField) => (
        <FilterFieldBlock
            key={field.name}
            field={field}
            schema={visibleFields}
            values={values}
            onChange={onChange}
        />
    );

    /* ---- active filter summary ---- */
    // Only over the fields the toolbar owns: a filter the page shows on a column
    // header already says it is active there, and repeating it as a chip is
    // noise. Computed even when chips are hidden, for the button's count.
    const activeChips = useMemo(() => {
        const chips: { key: string; label: string; clear: () => void }[] = [];
        panelFields.forEach((field) => {
            if (!isFieldActive(field, values)) return;
            const clear = () => clearFields([field], visibleFields, values, onChange);

            if (field.type === 'dateRange') {
                const from = values[field.startName];
                const to = values[field.endName];
                chips.push({
                    key: field.name,
                    label: `${field.label}: ${from ? new Date(from).toLocaleDateString() : '…'} – ${to ? new Date(to).toLocaleDateString() : '…'}`,
                    clear,
                });
                return;
            }

            const value = values[field.name];
            let display = String(value);
            if (field.type === 'select') {
                const list = Array.isArray(value) ? value : String(value).split(',');
                display = list.length > 2 ? `${list.length} selected` : list.join(', ');
            }
            if (field.type === 'toggle') display = 'on';
            chips.push({ key: field.name, label: `${field.label}: ${display}`, clear });
        });
        return chips;
    }, [panelFields, visibleFields, values]);

    // "Clear all" is a reset, so it reaches every filter the page has — not only
    // the ones drawn here — and shows whenever any of them is set
    const anyActive = visibleFields.some((field) => isFieldActive(field, values));
    const clearAll = () => clearFields(visibleFields, visibleFields, values, onChange);

    const clearAllButton = (variant: string) => (
        showClearAll && anyActive
            ? (
                <Button className={`ft-clear ${variant}`} type="link" size="small" onClick={clearAll}>
                    Clear all
                </Button>
            )
            : null
    );

    /* ---- drawer layout: one button, every field in a side panel ---- */
    if (layout === 'drawer') {
        // With the trigger elsewhere and nothing active, there is nothing left
        // for the bar to hold — so it holds no space either.
        const showBar = !hideTrigger || !!extra || (showActiveChips && activeChips.length > 0);

        return (
            <div
                className={`ft-toolbar ft-toolbar--drawer${showBar ? '' : ' ft-toolbar--bare'}${className ? ` ${className}` : ''}`}
                style={style}
            >
                {showBar && (
                    <div className="ft-row">
                        {/* the page's own controls lead; opening the panel is the
                            trailing action */}
                        {extra && <div className="ft-row__start">{extra}</div>}

                        {showActiveChips && activeChips.length > 0 && (
                            <div className="ft-active ft-active--inline">
                                {activeChips.map((chip) => (
                                    <Tag key={chip.key} className="ft-chip" closable onClose={chip.clear} closeIcon={<CloseOutlined />}>
                                        {chip.label}
                                    </Tag>
                                ))}
                                {clearAllButton('')}
                            </div>
                        )}
                        {!showActiveChips && clearAllButton('ft-clear--inline')}

                        {!hideTrigger && (
                            <div className="ft-row__end">
                                <FilterButton
                                    label={filterButtonLabel}
                                    count={activeChips.length}
                                    onClick={() => setDrawerOpen(true)}
                                />
                            </div>
                        )}
                    </div>
                )}

                <Drawer
                    title={drawerTitle ?? filterButtonLabel}
                    classNames={{ body: 'ft-drawer__body' }}
                    width={400}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    extra={clearAllButton('ft-clear--drawer')}
                >
                    {drawerDescription && <p className="ft-drawer__desc">{drawerDescription}</p>}
                    {panelFields.map(wrap)}
                    {/* things that shape the list without filtering it */}
                    {drawerFooter && <div className="ft-drawer__footer">{drawerFooter}</div>}
                </Drawer>
            </div>
        );
    }

    /* ---- inline layout ---- */
    const inline = collapseAfter ? panelFields.slice(0, collapseAfter) : panelFields;
    const overflow = collapseAfter ? panelFields.slice(collapseAfter) : [];
    const overflowActive = overflow.filter((f) => !isEmpty(values[f.name])).length;

    return (
        <div className={`ft-toolbar${className ? ` ${className}` : ''}`} style={style}>
            <div className="ft-row">
                {inline.map(wrap)}

                {overflow.length > 0 && (
                    <Popover
                        trigger="click"
                        placement="bottomRight"
                        classNames={{ root: 'ft-popover' }}
                        content={<div className="ft-popover__body">{overflow.map(wrap)}</div>}
                    >
                        <Badge count={overflowActive} size="small" offset={[-2, 2]}>
                            <Button className="ft-more" icon={<FilterOutlined />}>More filters</Button>
                        </Badge>
                    </Popover>
                )}

                {/* with chips hidden there is no summary row, so surface the
                    reset here rather than losing it entirely */}
                {!showActiveChips && clearAllButton('ft-clear--inline')}

                {extra && <div className="ft-row__end">{extra}</div>}
            </div>

            {showActiveChips && activeChips.length > 0 && (
                <div className="ft-active">
                    {activeChips.map((chip) => (
                        <Tag key={chip.key} className="ft-chip" closable onClose={chip.clear} closeIcon={<CloseOutlined />}>
                            {chip.label}
                        </Tag>
                    ))}
                    {showClearAll && (
                        <Tooltip title="Remove every active filter">
                            <Button className="ft-clear" type="link" size="small" onClick={clearAll}>
                                Clear all
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
        </div>
    );
};

export default FilterToolbar;
